import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Building2, KeyRound, Sparkles, ShieldCheck, Clock, Leaf, Star, ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
const heroImage = "/images/hero-cleaning.jpg";
const heroImage1280 = "/images/hero-cleaning-1280.jpg";

const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "DH Cleaning Services",
  description: "Melbourne's trusted professional cleaning services. Residential, commercial, end of lease & carpet cleaning.",
  url: "https://dh-cleaning-preview.lovable.app",
  telephone: "+61400000000",
  email: "info@dhcleaning.com.au",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Melbourne",
    addressRegion: "VIC",
    addressCountry: "AU",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: -37.8136,
    longitude: 144.9631,
  },
  areaServed: {
    "@type": "City",
    name: "Melbourne",
  },
  priceRange: "$$",
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "07:00",
    closes: "18:00",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "127",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Cleaning Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Residential Cleaning" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Commercial Cleaning" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "End of Lease Cleaning" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Carpet & Window Cleaning" } },
    ],
  },
};

const faqs = [
  { q: "What areas in Melbourne do you service?", a: "We service all of Melbourne and surrounding suburbs including the CBD, inner suburbs, and outer suburbs across the greater Melbourne metropolitan area." },
  { q: "Are you fully insured?", a: "Yes, DH Cleaning Services is fully insured with comprehensive public liability coverage, giving you complete peace of mind." },
  { q: "Do you use eco-friendly cleaning products?", a: "Absolutely. We use only eco-friendly, non-toxic cleaning products that are safe for your family, pets, and the environment." },
  { q: "How do I book a cleaning service?", a: "You can book online through our website by visiting the Book a Clean page, or call us directly on 0400 000 000. We also offer free, no-obligation quotes." },
  { q: "What is included in an end of lease clean?", a: "Our end of lease clean covers every area of your property including kitchens, bathrooms, floors, windows, oven degreasing, wall spot cleaning, and more — designed to help you get your full bond back." },
  { q: "Can I get a free quote before booking?", a: "Yes! We offer free, no-obligation quotes. Simply fill out our online quote form or give us a call, and we'll provide a detailed estimate based on your property and requirements." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: { "@type": "Answer", text: faq.a },
  })),
};

const services = [
  { icon: Home, title: "Residential Cleaning", desc: "Regular home cleaning tailored to your needs. We leave every room sparkling." },
  { icon: Building2, title: "Commercial Cleaning", desc: "Keep your workplace pristine. Offices, retail, and commercial spaces." },
  { icon: KeyRound, title: "End of Lease Cleaning", desc: "Get your full bond back with our thorough end-of-lease clean." },
  { icon: Sparkles, title: "Carpet & Window Cleaning", desc: "Deep carpet cleaning and streak-free windows for a fresh finish." },
];

const whyUs = [
  { icon: ShieldCheck, title: "Fully Insured", desc: "Complete peace of mind with full insurance coverage." },
  { icon: Clock, title: "Flexible Scheduling", desc: "Book at times that suit you, including weekends." },
  { icon: Leaf, title: "Eco-Friendly Products", desc: "Safe for your family, pets, and the environment." },
  { icon: Star, title: "5-Star Rated", desc: "Trusted by hundreds of happy customers across Melbourne." },
];

const testimonials = [
  { name: "Sarah M.", rating: 5, text: "Absolutely fantastic job! My house has never looked this clean. The team was professional and thorough." },
  { name: "James L.", rating: 5, text: "Used DH for our end-of-lease clean. Got our full bond back — couldn't be happier!" },
  { name: "Priya K.", rating: 5, text: "Reliable, friendly, and always on time. They use great eco-friendly products too." },
  { name: "Michael R.", rating: 4, text: "Great commercial cleaning service. Our office always looks immaculate after their visit." },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const Index = () => {
  return (
    <Layout>
      <SEO
        title="Professional Cleaning Services Melbourne"
        description="Melbourne's trusted cleaning professionals. Residential, commercial, end of lease & carpet cleaning. Fully insured, eco-friendly. Book online or get a free quote."
        canonical="/"
        keywords="cleaning services Melbourne, house cleaning Melbourne, commercial cleaning, end of lease cleaning, carpet cleaning, window cleaning, eco-friendly cleaning"
        jsonLd={[localBusinessJsonLd, faqJsonLd]}
        preloadImage={heroImage}
      />
      {/* Hero */}
      <section className="relative overflow-hidden py-20 lg:py-28">
        <div className="absolute inset-0">
          <img src={heroImage} srcSet={`${heroImage1280} 1280w, ${heroImage} 1920w`} sizes="100vw" alt="Professional cleaning team at work in a modern home" className="w-full h-full object-cover" fetchPriority="high" width={1920} height={835} decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40" />
        </div>
        <div className="container relative z-10">
          <div className="max-w-2xl">
            <motion.div {...fadeUp}>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5" /> Professional Cleaning Services
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1] mb-6">
                A Cleaner Space,<br />
                <span className="text-primary">A Happier Place</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Melbourne's trusted cleaning professionals. Residential, commercial, and end-of-lease cleaning — done right, every time.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button size="lg" asChild className="text-base px-6">
                  <Link to="/quote">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="text-base px-6">
                  <Link to="/services">Our Services</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 lg:py-24" aria-labelledby="services-heading">
        <div className="container">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 id="services-heading" className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-3">Our Services</h2>
            <p className="text-muted-foreground max-w-md mx-auto">From homes to offices, we have you covered with top-quality cleaning solutions.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <motion.div key={s.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Card className="h-full border-2 border-transparent hover:border-primary/20 transition-colors group">
                  <CardContent className="p-6 text-center">
                    <div className="mx-auto mb-4 h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <s.icon className="h-7 w-7 text-primary" aria-hidden="true" />
                    </div>
                    <h3 className="font-display font-semibold text-lg mb-2 text-foreground">{s.title}</h3>
                    <p className="text-sm text-muted-foreground">{s.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button variant="outline" asChild>
              <Link to="/services">View All Services <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24 bg-muted/50" aria-labelledby="why-us-heading">
        <div className="container">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 id="why-us-heading" className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-3">Why Choose DH Cleaning?</h2>
            <p className="text-muted-foreground max-w-md mx-auto">We go above and beyond to deliver exceptional cleaning results.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.map((item, i) => (
              <motion.div key={item.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-accent/10 flex items-center justify-center">
                  <item.icon className="h-7 w-7 text-accent" aria-hidden="true" />
                </div>
                <h3 className="font-display font-semibold text-lg mb-1 text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24" aria-labelledby="testimonials-heading">
        <div className="container">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 id="testimonials-heading" className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-3">What Our Customers Say</h2>
            <p className="text-muted-foreground max-w-md mx-auto">Don't just take our word for it — hear from our happy clients.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="flex gap-0.5 mb-3" role="img" aria-label={`${t.rating} out of 5 stars`}>
                      {Array.from({ length: 5 }).map((_, si) => (
                        <Star key={si} className={`h-4 w-4 ${si < t.rating ? "fill-accent text-accent" : "text-border"}`} aria-hidden="true" />
                      ))}
                    </div>
                    <blockquote>
                      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">"{t.text}"</p>
                      <cite className="font-display font-semibold text-sm text-foreground not-italic">{t.name}</cite>
                    </blockquote>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-muted/50" aria-labelledby="faq-heading">
        <div className="container max-w-3xl">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 id="faq-heading" className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-3">Frequently Asked Questions</h2>
            <p className="text-muted-foreground max-w-md mx-auto">Got questions? We've got answers.</p>
          </motion.div>
          <motion.div {...fadeUp}>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border rounded-lg px-5 bg-background">
                  <AccordionTrigger className="text-left font-display font-semibold text-foreground hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 lg:py-20 bg-primary text-primary-foreground">
        <div className="container text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">Ready for a Spotless Space?</h2>
            <p className="text-lg opacity-90 mb-8 max-w-lg mx-auto">Book your professional clean today and experience the DH difference.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button size="lg" variant="secondary" asChild className="text-base px-6 bg-background text-foreground hover:bg-background/90">
                <Link to="/booking">Book Your Clean <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button size="lg" asChild className="text-base px-6 bg-transparent border-2 border-white text-white hover:bg-white/20">
                <a href="tel:+61400000000">Call Us Now</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
